import type { AlertFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { mustHaveMaxLength } from '@/utils/validx-util'
import { validationContext } from 'validx'

/**
 * View Model for the Remove Client Team dialog.
 */
export class RemoveClientTeamDialogViewModel {
  /**
   * Holds the maximum length of each field.
   */
  private readonly _fieldLengths = {
    reason: { max: 100 },
  }

  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * Validation context for the view model.
   */
  readonly validation = validationContext(this, {
    reason: [
      mustHaveMaxLength(
        this._fieldLengths.reason.max,
        `The value can't be greater than ${this._fieldLengths.reason.max} characters.`,
      ),
    ],
  })

  /**
   * Helper text for reason field.
   */
  readonly reasonHelpText =
    'Why are you removing the team? This text is public and can be seen by both Client and Pros.'

  /**
   * The reason for the removal of the client team.
   */
  @observable
  accessor reason = ''

  constructor(
    private readonly _job: Job,
    private readonly _jobStore: JobStore,
    private readonly _newAlert: AlertFactory,
  ) {}

  /**
   * Getter for the current job client team.
   */
  @computed
  get currentClientTeam() {
    return this._job.ownerTeam
  }

  /**
   * Getter for the current job client team name.
   */
  @computed
  get currentClientTeamName() {
    return this._job.ownerTeam!.name
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.reason = ''

    this.validation.reset()
  }

  /**
   * Removes client team from Job.
   */
  removeClientTeam = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const alert = this._newAlert()
      .withState('pending')
      .saying(
        'Removing job from client team',
        `Removing ${this._job.ownerTeam?.name}...`,
      )
      .autoDismiss()
      .show()

    try {
      await this._jobStore.removeClientTeamAsAdmin(this._job.id, this.reason)
    } catch (err) {
      alert.dismiss()
      throw err
    }

    alert
      .withState('success')
      .saying('Success!', `Removed job from ${this._job.ownerTeam?.name}.`)
      .autoDismiss()

    this.modal.close()
  })

  /**
   * Shows the Remove Client Team Dialog.
   */
  @action.bound
  show() {
    if (this.modal.showing) {
      return
    }

    this.modal.show()
  }
}
